import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../../../style/Dentisstyle.css";
import { apiAxios } from "../../../../utils/apiAxios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface User {
  userID: number;
  name: string;
  email: string;
  mobileNo: string;
  address?: string;
  roleID?: number;
  roleName?: string;
  isApproved?: boolean | null;
  isRejected?: boolean | null;
  createdDate: string;
}

const UsersApproval: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const token = sessionStorage.getItem("token") || "";

  // Fetch Milling Hub Users
  const fetchUsers = async () => {
    try {
      setLoading(true);

      const res = await apiAxios.get(
        `${API_BASE}/Users/GetMillingHubUsers`
      );

      const apiData = res.data.data || res.data;

      if (Array.isArray(apiData)) {
        const sortedUsers = apiData.sort(
          (a: User, b: User) =>
            new Date(b.createdDate).getTime() -
            new Date(a.createdDate).getTime()
        );

        setUsers(sortedUsers);
        setFilteredUsers(sortedUsers);
      } else {

        setUsers([]);
        setFilteredUsers([]);
      }
    } catch (error) {
      toast.error("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Search Filter
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
    } else {
      const lower = searchTerm.toLowerCase();
      const filtered = users.filter(
        (u) =>
          u.name.toLowerCase().includes(lower) ||
          u.email.toLowerCase().includes(lower) ||
          u.mobileNo.toLowerCase().includes(lower)
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  // Approve User
  const handleApprove = async (userID: number) => {
    try {
      const res = await apiAxios.put(
        `${API_BASE}/Users/ApproveUser/${userID}`,
        {},
      );

      if (res.data.message?.toLowerCase().includes("success")) {
        toast.success("User approved successfully!");
        setShowModal(false);
        fetchUsers();
      } else {
        toast.warning(res.data.message || "Unable to approve user.");
      }
    } catch (error) {
      console.error("Approve error:", error);
      toast.error("Error approving user.");
    }
  };

  // Status Badge
  const getStatusBadge = (user: User) => {
    if (user.isApproved)
      return <span className="badge bg-success approved-btnpadding">Approved</span>;
    if (user.isRejected)
      return <span className="badge bg-danger approved-btnpadding">Rejected</span>;
    return <span className="badge bg-warning text-dark approved-btnpadding">Pending</span>;
  };

  // DataTable Columns
  const columns: TableColumn<User>[] = [
    {
      name: "User ID",
      cell: (row) =>
        !row.isApproved && !row.isRejected ? (
          <a
            onClick={() => {
              setSelectedUser(row);
              setShowModal(true);
            }}
            className="tobe-verify-text"
            style={{ cursor: "pointer" }}
          >
            To Be Verified
          </a>
        ) : (
          <span>{row.userID}</span>
        ),
      minWidth: "130px",
      center: true,
    },
    { name: "Full Name", minWidth: "150px", center: true, selector: (row) => row.name, sortable: true },
    { name: "Email", minWidth: "150px", center: true, selector: (row) => row.email, sortable: true },
    { name: "Mobile", minWidth: "150px", selector: (row) => row.mobileNo, center: true },
    {
      name: "Status",
      minWidth: "150px",
      cell: (row) => getStatusBadge(row),
      center: true,
    },
  ];

  const customStyles = {
    // headRow: { style: { backgroundColor: "#3D8BFD", color: "white" } },
    headCells: { style: { fontSize: "15px", justifyContent: "center" } },
    cells: { style: { fontSize: "14px" } },
  };

  return (
    <div className="min-h-screen">
      <div className="premium-card">
        <h4 className="mb-4 ">Milling Hub Approvals</h4>

        {/* Search Bar */}
        <div className="row mb-3">
          <div className="col-md-6 col-12 col-lg-3">
            <div className="input-group position-relative">
              <input
                type="text"
                className="form-control"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => setSearchTerm("")}
              >
                <i className="bi bi-search"></i>
              </button>
            </div>
          </div>
        </div>

        {/* DataTable */}
        <DataTable
          columns={columns}
          data={filteredUsers}
          progressPending={loading}
          pagination
          highlightOnHover
          pointerOnHover
          onRowClicked={(row) => {
            setSelectedUser(row);
            setShowModal(true);
          }}
          noDataComponent="No Milling Hub users found."
          className="admin-delivery-table"
        />
      </div>

      {/* Approval Modal */}
      {showModal && selectedUser && (
        <>
          {/* Backdrop */}
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1040 }}
          ></div>

          <div
            className="modal show d-block"

            style={{ zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ width: "100%", maxWidth: "500px", margin: "auto" }}>
                <div className="modal-header closebtn-and-deletebtndent text-white">
                  <h5 className="modal-title customdelete-title">
                    MillingHub Details — UserID {selectedUser.userID}
                  </h5>



                  <button
                    type="button"
                    className="btn-close custom-close-btn"
                    data-bs-dismiss="modal"
                    aria-label="Close" onClick={() => setShowModal(false)}
                  ><i className="bi bi-x customcross"></i></button>
                </div>

                <div className="modal-bodyclinic dentist-modalbody">
                  <ul className="clinic-verificationlist">
                    <li className="clinic-namefontweight">Full Name</li>
                    <li>{selectedUser.name}</li>
                  </ul>
                <ul className="clinic-verificationlist">
  <li className="clinic-namefontweight">Address</li>
  <li className="clinic-address">
    {selectedUser.address || "-"}
  </li>
</ul>
                  <ul className="clinic-verificationlist">
                    <li className="clinic-namefontweight">Email</li>
                    <li>{selectedUser.email}</li>
                  </ul>
                  <ul className="clinic-verificationlist">
                    <li className="clinic-namefontweight">Mobile</li>
                    <li>{selectedUser.mobileNo}</li>
                  </ul>
                  <ul className="clinic-verificationlist">
                    <li className="clinic-namefontweight">Status</li>
                    <li>{getStatusBadge(selectedUser)}</li>
                  </ul>
                </div>

                <div className="mt-2 py-3 btn-sectionfooter">
                  {!selectedUser.isApproved && !selectedUser.isRejected ? (
                    <button
                      onClick={() => handleApprove(selectedUser.userID)}
                      className="btn-primary py-2 btn"
                    >
                      Approve
                    </button>
                  ) : (
                    <button
                      className="btn btn-secondary py-2"
                      onClick={() => setShowModal(false)}
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UsersApproval;
